import React from 'react';
import { MessageCircle, Calendar, CheckCircle, Shield, Clock, Tag, Home, Building2, Archive } from 'lucide-react';
import ServicePageLayout from '../components/ServicePageLayout';
import { P } from '../constants';

const data = {
  seo: {
    title: 'Wohnungsräumung Wien » Fixpreis ✓ Heute verfügbar – Wohnungsräumung Wien',
    description: 'Professionelle Wohnungsräumung in Wien ✓ Kostenlose Besichtigung ✓ Verbindlicher Fixpreis ✓ Besenrein. 1–4 Zimmer ab €290. Noch heute anfragen!',
    keywords: 'Wohnungsräumung Wien, Wohnungsräumung Wien Kosten, Wohnungsräumung Wien Fixpreis, Wohnung räumen Wien',
    canonical: '/wohnungsraeumung-wien',
    h1short: 'Wohnungsräumung Wien',
  },
  schemas: [{
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Wohnungsräumung Wien',
    'provider': { '@type': 'LocalBusiness', 'name': 'Wohnungsräumung Wien' },
    'areaServed': 'Wien',
    'description': 'Professionelle Wohnungsräumung in Wien mit Fixpreisgarantie, kostenloser Besichtigung und besenreiner Übergabe.',
    'offers': { '@type': 'Offer', 'price': '490', 'priceCurrency': 'EUR' },
  }],
  hero: {
    badge: 'Termine kurzfristig möglich',
    headline: <><span>Wohnungsräumung<br />Wien</span><br /><span className="text-[#E8722A]">zum Fixpreis.</span></>,
    subline: 'Wir kommen. Wir räumen. Sie haben Ruhe.',
    bullets: [
      'Kostenlose Besichtigung. Verbindlicher Fixpreis. Keine Ausreden.',
      'Kurzfristige Termine – oft noch diese Woche',
      'Gut erhaltene Sachen werden bewertet und abgezogen',
      'Besenreine Übergabe. Immer. Ohne Aufpreis.',
      'Seit 2010 in Wien. Über 2.000 Räumungen.',
    ],
    imageSrc: P.clutteredRoom,
    imageAlt: 'Wohnungsräumung Wien – Zu räumende Wohnung',
  },
  formTitle: 'Wohnungsräumung anfragen –',
  serviceName: 'Wohnungsräumung',
  serviceCard: {
    heading: 'Wohnungsräumung Wien – wir kommen, räumen, übergeben',
    paragraphs: [
      'Wohnungsübergabe steht an – und noch nichts geräumt? Todesfall, Umzug, Trennung. Egal, was der Anlass ist. Wir stellen keine Fragen. Wir räumen.',
      'Einmal anrufen. Wir schauen uns alles an, nennen Ihnen einen verbindlichen Preis – und halten ihn. Keine Nachforderungen. Kein Kleingedrucktes.',
    ],
    listItems: [
      'Alles raus – Möbel, Hausrat, Sperrmüll, Keller & Abstellraum',
      'Gute Stücke werden bewertet und direkt vom Preis abgezogen',
      'Sortenreine Entsorgung – wir kümmern uns um alles',
      'Besenreine Übergabe – bereit für den nächsten Mieter',
      'Sie müssen nicht dabei sein – Schlüssel übergeben, Ergebnis abholen',
    ],
    imageSrc: P.emptyRoom,
    imageAlt: 'Geräumte Wohnung Wien – besenrein übergeben',
  },
  benefits: [
    { icon: <Shield size={22} />, title: 'Fixpreisgarantie', desc: 'Kein Cent über das vereinbarte Angebot hinaus. Verbindlicher Preis nach Besichtigung.' },
    { icon: <Clock size={22} />, title: '24h Verfügbarkeit', desc: 'Kurzfristige Termine möglich – in Notfällen noch am selben Tag.' },
    { icon: <Tag size={22} />, title: 'Wertanrechnung', desc: 'Gut erhaltene Möbel & Elektrogeräte werden direkt vom Gesamtpreis abgezogen.' },
    { icon: <CheckCircle size={22} />, title: 'Besenreine Übergabe', desc: 'Nach jeder Räumung übergeben wir die Wohnung besenrein – immer inklusive.' },
  ],
  process: [
    { icon: <MessageCircle size={24} />, title: 'Anfrage senden', desc: 'Formular ausfüllen oder anrufen – dauert 2 Minuten. Kein Aufwand, keine Verpflichtung.' },
    { icon: <Calendar size={24} />, title: 'Kostenlose Besichtigung & Fixpreis', desc: 'Wir kommen vorbei, schauen alles an und geben Ihnen einen verbindlichen Festpreis.' },
    { icon: <CheckCircle size={24} />, title: 'Räumung & besenreine Übergabe', desc: 'Unser Team räumt vollständig – inklusive Entsorgung und besenreiner Übergabe.' },
  ],
  pricing: [
    { label: 'Keller / Dachboden', price: 'ab €290', icon: <Archive size={20} />, highlight: false },
    { label: '1–2 Zimmer Wohnung', price: 'ab €490', icon: <Home size={20} />, highlight: true },
    { label: '3+ Zimmer Wohnung', price: 'ab €890', icon: <Building2 size={20} />, highlight: false },
  ],
  faq: [
    { q: 'Was kostet eine Wohnungsräumung in Wien?', a: 'Ab €290 für Keller, ab €490 für kleine Wohnungen, ab €890 für größere. Nach der kostenlosen Besichtigung erhalten Sie den Fixpreis – und den halten wir.' },
    { q: 'Wie schnell können Sie kommen?', a: 'Oft noch diese Woche. Bei Notfällen melden wir uns innerhalb von Stunden. Einfach anrufen.' },
    { q: 'Muss ich bei der Räumung dabei sein?', a: 'Nein. Schlüssel übergeben, wir erledigen alles – und übergeben besenrein. Sie kommen zurück und die Wohnung ist leer und sauber.' },
    { q: 'Was passiert mit brauchbaren Möbeln?', a: 'Gut erhaltene Stücke bewerten wir vor Ort und ziehen den Wert vom Gesamtpreis ab. Wertanrechnung heißt das – und sie spart Ihnen bares Geld.' },
    { q: 'Ist die Wohnung danach besenrein?', a: 'Ja. Immer. Ohne Aufpreis. Das ist Standard bei uns.' },
  ],
  relatedServices: [
    { title: 'Haushaltsauflösung', slug: '/haushaltsaufloesung-wien', icon: <Archive size={18} /> },
    { title: 'Nachlassräumung', slug: '/nachlassraeumung-wien', icon: <CheckCircle size={18} /> },
    { title: 'Messie-Wohnung', slug: '/messie-wohnung-wien', icon: <Building2 size={18} /> },
    { title: 'Kellerräumung', slug: '/kellerraeumung-wien', icon: <Home size={18} /> },
  ],
};

export default function WohnungsraeumungPage() {
  return <ServicePageLayout data={data} />;
}
